import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/datetime";
import { errMsg } from "@/components/email/EmParts";
import StarterGallery from "@/components/email/StarterGallery";

const TABS = [["", "All"], ["draft", "Drafts"], ["scheduled", "Scheduled"], ["sending", "Sending"], ["paused", "Paused"], ["sent", "Sent"], ["failed", "Failed"], ["cancelled", "Cancelled"]];

export function CampaignTable({ rows, testId = "em-campaign-table" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead><tr className="text-[11px] uppercase text-slate-400 text-left">{["Campaign", "Status", "Recipients", "Sent", "Delivered", "Opens*", "Clicks", "Unsubs", "Bounces", "Date"].map((h) => <th key={h} className="py-2 px-3">{h}</th>)}</tr></thead>
        <tbody>{rows.map((c) => (
          <tr key={c.id} className="border-t border-slate-100" data-testid={`em-campaign-row-${c.id}`}>
            <td className="py-2 px-3"><Link to={c.status === "draft" ? `/email-marketing/campaigns/${c.id}` : `/email-marketing/campaigns/${c.id}/report`} className="font-medium text-slate-800 hover:text-indigo-700" data-testid={`em-campaign-link-${c.id}`}>{c.name}</Link><div className="text-xs text-slate-400 truncate max-w-[240px]">{c.subject}</div></td>
            <td className="px-3 capitalize text-xs">{c.status}</td>
            {["recipients", "sent", "delivered", "opened", "clicked", "unsubscribed", "bounced"].map((k) => <td key={k} className="px-3 tabular-nums">{(c.stats?.[k] || 0).toLocaleString()}</td>)}
            <td className="px-3 text-xs text-slate-500">{formatDateTime(c.scheduled_at || c.updated_at)}</td></tr>))}
          {!rows.length && <tr><td colSpan={10} className="py-6 text-center text-slate-400" data-testid={`${testId}-empty`}>No campaigns.</td></tr>}</tbody>
      </table>
    </div>
  );
}

export default function EmCampaigns() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/email-marketing/campaigns", { params: { status: tab } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [tab]);
  const create = async () => {
    try { const { data } = await api.post("/email-marketing/campaigns", { name: `New campaign ${new Date().toLocaleDateString()}` }); nav(`/email-marketing/campaigns/${data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rows.filter((c) => !q || `${c.name} ${c.subject}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="em-campaigns">
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === k ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`em-campaign-tab-${k || "all"}`}>{l}</button>)}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search campaigns" className="h-8 rounded-md border border-slate-200 px-2 text-sm" data-testid="em-campaign-search" />
        {can("email_marketing.manage") && <Button className="gap-1 ml-auto" onClick={create} data-testid="em-new-campaign-btn"><Plus className="w-4 h-4" />New campaign</Button>}
      </div>
      {can("email_marketing.manage") && <StarterGallery mode="campaign" testId="em-campaign-starters" />}
      <CampaignTable rows={shown} />
      <p className="text-[11px] text-slate-400">*Opens are estimates: privacy protection and image blocking affect open tracking.</p>
    </div>
  );
}
